import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Plug2, CheckCircle2, RefreshCw, Database, Info } from 'lucide-react';
import { cn } from '@/lib/utils';
import { mockDatabaseInstances } from '@/types/integrations';
import type { IntegrationsTabProps } from './types';

export function IntegrationsTab({
  integrations,
  selectedIntegration,
  selectedInstance,
  loadingInstances,
  loadedInstances,
  onIntegrationToggle,
  onInstanceChange,
  getEnvironmentBadgeVariant,
}: Readonly<IntegrationsTabProps>): React.ReactElement {
  return (
    <div className="space-y-6">
      <Card className="border border-gray-200 dark:border-gray-700">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Plug2 className="h-5 w-5 text-primary" />
            Financial System Integrations
          </CardTitle>
          <CardDescription>
            Connect your assets to a financial system to sync data and streamline reporting.
            Only one integration can be active at a time.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {integrations.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-muted">
                <Plug2 className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="mb-2 text-lg font-semibold">No Integrations Available</h3>
              <p className="max-w-sm text-sm text-muted-foreground">
                Connect your assets to a financial system to sync data and streamline reporting
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Integration Cards Grid */}
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {integrations.map((integration) => {
                  const isSelected = selectedIntegration === integration.id;
                  const isDisabled = integration.comingSoon || (selectedIntegration !== null && !isSelected);

                  return (
                    <div
                      key={integration.id}
                      className={cn(
                        'relative overflow-hidden rounded-lg border-2 transition-all',
                        isSelected && 'border-primary bg-primary/5',
                        !isSelected && !isDisabled && 'border-border hover:border-primary/50',
                        isDisabled && 'opacity-60'
                      )}
                    >
                      <div className="p-6">
                        {/* Header */}
                        <div className="mb-4 flex items-start justify-between">
                          <div className="flex items-center gap-3">
                            <div className={cn(
                              'flex h-12 w-12 items-center justify-center rounded-lg overflow-hidden',
                              isSelected ? 'bg-primary/10 ring-2 ring-primary' : 'bg-muted'
                            )}>
                              {integration.logo ? (
                                <img
                                  src={integration.logo}
                                  alt={`${integration.name} logo`}
                                  className="h-8 w-8 object-contain"
                                  onError={(e) => {
                                    e.currentTarget.style.display = 'none';
                                    e.currentTarget.parentElement!.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-6 w-6"><ellipse cx="12" cy="5" rx="9" ry="3"></ellipse><path d="M3 5V19A9 3 0 0 0 21 19V5"></path><path d="M3 12A9 3 0 0 0 21 12"></path></svg>';
                                  }}
                                />
                              ) : (
                                <Database className="h-6 w-6" />
                              )}
                            </div>
                            <div>
                              <h3 className="font-semibold">{integration.name}</h3>
                              {integration.comingSoon && (
                                <Badge variant="secondary" className="mt-1">
                                  Coming Soon
                                </Badge>
                              )}
                            </div>
                          </div>
                          <Switch
                            checked={isSelected}
                            onCheckedChange={() => onIntegrationToggle(integration.id)}
                            disabled={isDisabled}
                          />
                        </div>

                        {/* Description */}
                        <p className="mb-4 text-sm text-muted-foreground">
                          {integration.description}
                        </p>

                        {/* Features */}
                        {integration.features && integration.features.length > 0 && (
                          <div className="space-y-2">
                            <p className="text-xs font-medium text-muted-foreground">Key Features:</p>
                            <ul className="space-y-1">
                              {integration.features.slice(0, 3).map((feature) => (
                                <li key={`feature-${feature}`} className="flex items-start gap-2 text-xs text-muted-foreground">
                                  <CheckCircle2 className="mt-0.5 h-3 w-3 shrink-0 text-primary" />
                                  <span>{feature}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>

                      {/* Instance Selection Panel */}
                      {isSelected && !integration.comingSoon && (
                        <div className="border-t bg-muted/50 p-4">
                          <div className="space-y-3">
                            <Label htmlFor={`instance-${integration.id}`} className="text-sm font-medium">
                              Database Instance
                            </Label>

                            {loadingInstances && (
                              <div className="flex items-center justify-center py-4">
                                <RefreshCw className="h-4 w-4 animate-spin text-muted-foreground" />
                              </div>
                            )}
                            {!loadingInstances && (loadedInstances[integration.id]?.length > 0 || mockDatabaseInstances[integration.id]?.length > 0) && (
                              <Select
                                value={selectedInstance}
                                onValueChange={onInstanceChange}
                              >
                                <SelectTrigger id={`instance-${integration.id}`}>
                                  <SelectValue placeholder="Select an instance" />
                                </SelectTrigger>
                                <SelectContent>
                                  {(loadedInstances[integration.id]?.length > 0 ? loadedInstances[integration.id] : mockDatabaseInstances[integration.id]).map((instance) => (
                                    <SelectItem key={instance.id} value={instance.id}>
                                      <div className="flex items-center gap-2">
                                        <span>{instance.name}</span>
                                        {instance.environment && (
                                          <Badge
                                            variant={getEnvironmentBadgeVariant(instance.environment)}
                                            className="text-xs"
                                          >
                                            {instance.environment}
                                          </Badge>
                                        )}
                                      </div>
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                            {!loadingInstances && !(loadedInstances[integration.id]?.length > 0 || mockDatabaseInstances[integration.id]?.length > 0) && (
                              <Alert>
                                <Info className="h-4 w-4" />
                                <AlertDescription className="text-xs">
                                  No database instances found. Please configure your connection settings.
                                </AlertDescription>
                              </Alert>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
